import * as THREE from "three";
import { ACCENTS, BLUR, CAMERA, CHEST, DAY, DEFAULT_WORLD, DITHER, EXPLORED, FIRE, LIGHT, MOVE, NIGHT, PALETTE, PARTY, RUNTIME, SIGHT, TINTS, TORCH, WORLD } from "./config";
import { sampleDay } from "./world/daylight";
import { IsoCamera } from "./camera";
import { Controls } from "./input";
import { createLighting, type Lighting } from "./lighting";
import { Presenter } from "./renderer";
import type { ViewFrame } from "./post";
import { createRng } from "./rng";
import { buildSceneStaged, type SceneBuild } from "./scene";
import type { ChestItem } from "./world/chest";
import { ITEMS } from "./items";
import type { AccentOptions, DitherOptions } from "./types";
import { createToast, type Toast } from "./ui/toast";
import { Explored } from "./world/explored";
import { connectSession, type Session } from "./net/session";
import { buildRemote, type RemotePlayer } from "./actors/remote";
import { PartyHud, type ScreenBox } from "./ui/party";
import { Prompt, type PromptKey } from "./ui/prompt";
import { Inventory } from "./ui/inventory";
import { Tooltip } from "./ui/tooltip";
import { flashClock } from "./ui/clock";
import type { ItemId } from "./items";

export interface AppOptions {
  seed?: number;
  dither?: Partial<DitherOptions>;
  resolution?: number;
  blur?: Partial<{ clearWorld: number; blurWorld: number; strength: number }>;
  accent?: AccentOptions;
  spawn?: [number, number];
  pose?: number;
  gait?: number;
  zoom?: number;
  time?: number;
  /** ?time= was passed explicitly, so it pins the hour even in a shared world */
  timePinned?: boolean;
  cycle?: number;
  world?: string;
  name?: string;
  party?: string;
}

const hexParam = (params: URLSearchParams, key: string, fallback: number): number => {
  const raw = params.get(key);
  if (raw === null) return fallback;
  const parsed = Number.parseInt(raw.replace(/^#/, ""), 16);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const numberParam = (params: URLSearchParams, key: string, fallback: number): number => {
  const raw = params.get(key);
  if (raw === null) return fallback;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
};

export function optionsFromLocation(location: Location): AppOptions {
  const params = new URLSearchParams(location.search);
  const dither: Partial<DitherOptions> = {};
  const keys: Array<keyof DitherOptions> = [
    "levels",
    "cell",
    "exposure",
    "gamma",
    "contrast",
    "bayerSize",
    "fog",
  ];
  for (const key of keys) {
    if (params.has(key)) dither[key] = numberParam(params, key, 0);
  }

  const blur: AppOptions["blur"] = {};
  if (params.has("clear")) blur.clearWorld = numberParam(params, "clear", 0);
  if (params.has("blurRadius")) blur.blurWorld = numberParam(params, "blurRadius", 0);
  if (params.has("blur")) blur.strength = numberParam(params, "blur", 0);

  const tintName = params.get("tint") ?? "mono";
  const preset = (TINTS as Record<string, { shadow: number; highlight: number }>)[tintName] ??
    TINTS.mono;
  dither.shadowTint = hexParam(params, "shadow", preset.shadow);
  dither.highlightTint = hexParam(params, "highlight", preset.highlight);

  const accentName = params.get("accent") ?? "ember";
  let accentColour: number = ACCENTS.ember;
  if (accentName !== null) {
    accentColour =
      (ACCENTS as Record<string, number>)[accentName] ??
      hexParam(params, "accent", ACCENTS.red);
  }
  const hours = numberParam(params, "time", DAY.start * 24);
  const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
  RUNTIME.lite = params.has("lite") ? params.get("lite") !== "0" : coarse;

  const accent: AccentOptions = {
    scheme: params.get("palette") ?? PALETTE.scheme,
    colour: hexParam(params, "accentColour", accentColour),
    strength: numberParam(params, "accentStrength", accentColour === ACCENTS.none ? 0 : 0.72),
    saturation: numberParam(params, "accentSaturation", 0.72),
    gain: numberParam(params, "accentGain", 1.18),
  };

  return {
    seed: numberParam(params, "seed", WORLD.seed),
    zoom: numberParam(params, "zoom", 1),
    time: hours / 24,
    timePinned: params.has("time"),
    cycle: numberParam(params, "cycle", DAY.seconds),
    // a world is always joined: no query string needed. ?solo escapes it.
    world: params.has("solo") ? undefined : params.get("world") || DEFAULT_WORLD,
    name: params.get("name") ?? defaultName(),
    party: params.get("party") ?? PARTY_HOST,
    resolution: numberParam(params, "res", 1),
    blur,
    dither,
    accent,
    spawn: params.has("x") ? [numberParam(params, "x", 0), numberParam(params, "z", 0)] : undefined,
    pose: params.has("pose") ? numberParam(params, "pose", 0) : undefined,
    gait: numberParam(params, "gait", 0),
  };
}

/** names for visitors who arrive without one, kept so it stays theirs */
const NAME_WORDS = [
  "ember", "flint", "moss", "reed", "hollow",
  "thistle", "bramble", "rowan", "sorrel", "wren",
];

function defaultName(): string {
  try {
    const saved = localStorage.getItem("explorer.name");
    if (saved) return saved;
    const made = `${NAME_WORDS[Math.floor(Math.random() * NAME_WORDS.length)]} ${
      10 + Math.floor(Math.random() * 89)
    }`;
    localStorage.setItem("explorer.name", made);
    return made;
  } catch {
    // storage can be unavailable when embedded; a name is not worth failing over
    return "wanderer";
  }
}

/**
 * Where the party server lives. Set VITE_PARTY_HOST when deploying (e.g.
 * explorer-party.username.partykit.dev); locally it falls back to the dev server.
 */
const PARTY_HOST =
  (import.meta.env.VITE_PARTY_HOST as string | undefined) ?? `${location.hostname}:1999`;

export class App {
  readonly scene: THREE.Scene;
  ready = false;
  fps = 0;

  private readonly canvas: HTMLCanvasElement;
  private readonly explored: Explored;
  private readonly session: Session | null = null;
  private readonly remotes = new Map<string, RemotePlayer>();
  private readonly party = new Set<string>();
  private readonly invites = new Map<string, { name: string; at: number }>();
  private readonly owned = new Set<ItemId>();
  private interactTarget: string | null = null;
  private holdTime = 0;
  private holdFired = false;
  private waitingOn: string | null = null;
  private sightRadius: number = EXPLORED.radius;
  private sightBlend = 0;
  private glassesOn = false;
  private readonly partyHud = new PartyHud();
  private readonly prompt = new Prompt();
  private readonly inventory: Inventory;
  private readonly tooltip = new Tooltip();
  private readonly picker = new THREE.Raycaster();
  private readonly pointer = { x: 0, y: 0 };
  private readonly projected = new THREE.Vector3();
  private netLevel = 1;
  private readonly presenter: Presenter;
  private readonly iso: IsoCamera;
  private readonly lighting: Lighting;
  private readonly keyboard = new Controls();
  private readonly accentStrength: number;
  private readonly debugPose: number | undefined;
  private readonly build: SceneBuild;
  private readonly velocity = new THREE.Vector3();
  private readonly desired = new THREE.Vector3();
  private readonly target = new THREE.Vector3();
  private readonly toast: Toast;
  private readonly footOffset: number;
  private readonly waypoint = new THREE.Vector3();
  private readonly raycaster = new THREE.Raycaster();
  private readonly marker: THREE.Mesh;

  private hasWaypoint = false;
  private footY = 0;
  private stepArmed = true;
  private markerAge = Number.POSITIVE_INFINITY;

  private announced: string | null = null;
  private readonly cycle: number;
  private readonly clockOffset: number;
  private readonly themeTimer = { value: 0, colour: 0 };

  private clock: number = DAY.start;
  private sunTimer = 0;
  private sunAzimuth: number = LIGHT.azimuth;
  private sunElevation: number = LIGHT.elevation;
  private lastTime = 0;
  private elapsed = 0;
  private frames = 0;
  private accumulator = 0;
  private sunAnchor = new THREE.Vector3(Number.POSITIVE_INFINITY, 0, 0);

  static async create(
    canvas: HTMLCanvasElement,
    options: AppOptions = {},
    onProgress?: (fraction: number) => void,
  ): Promise<App> {
    const rng = createRng(options.seed ?? WORLD.seed);
    const build = await buildSceneStaged(rng, onProgress ?? ((): void => {}));
    return new App(canvas, options, build);
  }

  private constructor(canvas: HTMLCanvasElement, options: AppOptions, build: SceneBuild) {
    this.canvas = canvas;
    this.explored = new Explored();
    this.presenter = new Presenter(
      canvas,
      { ...DITHER, ...options.dither },
      {
        clearWorld: options.blur?.clearWorld ?? BLUR.clearWorld,
        blurWorld: options.blur?.blurWorld ?? BLUR.blurWorld,
        midMix: BLUR.midMix,
        mipScale: BLUR.mipScale,
        strength: options.blur?.strength ?? BLUR.blurStrength,
      },
      options.resolution ?? 1,
    );

    this.build = build;
    this.scene = build.scene;
    this.presenter.attachExplored(this.explored.texture);
    this.footOffset = build.character.footOffset;

    this.iso = new IsoCamera();
    this.iso.setFrustum(CAMERA.frustumHeight * (options.zoom ?? 1));
    this.lighting = createLighting(this.scene);
    this.keyboard.attach(canvas);
    this.accentStrength = options.accent?.strength ?? 0;
    this.cycle = Math.max(30, options.cycle ?? DAY.seconds);
    // the world clock is a function of wall time, so every client agrees without
    // any sync traffic. A time offset only applies in single-player.
    const initialClock = ((options.time ?? DAY.start) % 1 + 1) % 1;
    // the world clock is wall time so everyone agrees — unless ?time= says
    // otherwise, which is what recording and screenshots need
    this.clockOffset =
      options.world && !options.timePinned
        ? 0
        : initialClock * this.cycle - Date.now() / 1000;
    this.clock = initialClock;
    this.debugPose = options.pose;
    this.build.character.setGait(options.gait ?? 0);
    this.presenter.setAccent(options.accent ?? {});
    this.toast = createToast();
    this.inventory = new Inventory(this.tooltip);
    this.inventory.onStoreRequest((id) => this.store(id));
    this.inventory.onTakeRequest((id) => {
      const item = this.build.chest.items.find((entry) => entry.id === id && !entry.taken);
      if (item) this.collect(item);
    });
    this.inventory.onToggleRequest(() => {
      if (this.inventory.isOpen) {
        // the press that opened the chest is not also a press on its contents
        this.keyboard.consumePadConfirm();
      } else {
        this.closeChest();
      }
    });
    window.addEventListener("pointermove", (event) => {
      if (event.pointerType === "touch") return;
      this.pointer.x = event.clientX;
      this.pointer.y = event.clientY;
    });

    if (options.world) {
      this.session = connectSession(
        options.world,
        options.name ?? "wanderer",
        options.party ?? PARTY_HOST,
      );
    }
    this.marker = new THREE.Mesh(
      new THREE.PlaneGeometry(0.28, 0.28),
      new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0,
        depthTest: false,
      }),
    );
    this.marker.rotation.x = -Math.PI / 2;
    this.marker.renderOrder = 2;
    this.marker.visible = false;
    this.scene.add(this.marker);
    document.documentElement.style.setProperty(
      "--accent",
      `#${(options.accent?.colour ?? ACCENTS.ember).toString(16).padStart(6, "0")}`,
    );

    if (options.spawn) this.teleport(options.spawn[0], options.spawn[1]);

    const start = this.build.character.root.position;
    this.footY = this.build.ground.heightAt(start.x, start.z);
    start.y = this.footY + this.footOffset;
    this.iso.jumpTo(start);
    this.lighting.focus.copy(start);
    this.lighting.requestUpdate();
    this.sunAnchor.copy(start);

    this.resize();
  }

  configureDither(options: Partial<DitherOptions>): void {
    this.presenter.post.configure(options);
  }

  teleport(x: number, z: number): void {
    const root = this.build.character.root;
    this.footY = this.build.ground.heightAt(x, z);
    root.position.set(x, this.footY + this.footOffset, z);
    this.iso.jumpTo(root.position);
  }

  start(): void {
    window.addEventListener("resize", () => this.resize());
    requestAnimationFrame(this.frame);
  }

  private resize(): void {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    this.presenter.resize(width, height);
    const [bufferWidth, bufferHeight] = this.presenter.bufferSize;
    this.iso.setAspect(bufferWidth / bufferHeight);
  }

  private readonly frame = (now: number): void => {
    const delta = this.lastTime === 0 ? 0 : Math.min(0.05, (now - this.lastTime) / 1000);
    this.lastTime = now;
    this.elapsed += delta;

    this.step(delta);
    this.applyDaylight(delta);
    this.presenter.setView(this.viewFrame());
    this.presenter.render(this.scene, this.iso.camera, this.accentStrength);

    this.accumulator += delta;
    this.frames++;
    if (this.accumulator >= 0.5) {
      this.fps = Math.round(this.frames / this.accumulator);
      this.accumulator = 0;
      this.frames = 0;
    }

    if (!this.ready) this.ready = true;
    requestAnimationFrame(this.frame);
  };

  private step(delta: number): void {
    this.keyboard.pollGamepad(delta);
    const character = this.build.character;
    const root = character.root;
    if (this.keyboard.consumeInventory() > 0) this.inventory.toggle();
    const nav = this.keyboard.consumePadNav();
    if (this.inventory.isOpen && (nav.x !== 0 || nav.y !== 0)) {
      this.inventory.moveFocus(nav.x, nav.y);
    }
    if (this.inventory.isOpen && this.keyboard.consumePadConfirm() > 0) {
      this.inventory.confirmFocus();
    }
    if (this.keyboard.consumeDismiss() > 0) this.inventory.close();
    // measured from the chest, so running a lap around it does not shut the lid
    if (
      this.inventory.isOpen &&
      this.build.chest.opened &&
      Math.hypot(
        this.build.chest.group.position.x - root.position.x,
        this.build.chest.group.position.z - root.position.z,
      ) > CHEST.leaveRadius
    ) {
      this.inventory.close();
    }
    const input = this.keyboard.state;

    const steps = this.keyboard.consumeZoomSteps();
    if (steps !== 0) this.iso.stepZoom(steps);
    const zoom = this.keyboard.consumeZoom();
    if (zoom !== 1) this.iso.zoom(zoom);
    const orbit = this.keyboard.consumeOrbit();
    if (orbit !== 0) this.iso.orbit(orbit);

    const yaw = THREE.MathUtils.degToRad(this.iso.yaw);
    const forward = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));
    const right = new THREE.Vector3(-forward.z, 0, forward.x);

    this.desired
      .set(0, 0, 0)
      .addScaledVector(forward, input.moveY)
      .addScaledVector(right, input.moveX);

    let moving = this.desired.lengthSq() > 1e-6;
    let sprint = input.sprint;

    if (moving) {
      this.desired.normalize();
      this.clearWaypoint();
    } else if (this.hasWaypoint) {
      const dx = this.waypoint.x - root.position.x;
      const dz = this.waypoint.z - root.position.z;
      const distance = Math.hypot(dx, dz);
      if (distance < 0.35) {
        this.clearWaypoint();
      } else {
        this.desired.set(dx / distance, 0, dz / distance);
        moving = true;
        sprint = true;
      }
    }

    const tap = this.keyboard.consumeTap();
    if (tap) {
      const item = this.pickChestItem(tap);
      if (item) this.collect(item);
      else this.assignWaypoint(tap);
    }

    const speed = MOVE.speed * (sprint ? 1.65 : 1);
    this.target.copy(this.desired).multiplyScalar(moving ? speed : 0);
    const blend = 1 - Math.exp(-MOVE.acceleration * delta);
    this.velocity.lerp(this.target, blend);
    if (this.velocity.lengthSq() < 1e-5) this.velocity.set(0, 0, 0);

    root.position.addScaledVector(this.velocity, delta);
    const limit = WORLD.groundSize * 0.46;
    root.position.x = THREE.MathUtils.clamp(root.position.x, -limit, limit);
    root.position.z = THREE.MathUtils.clamp(root.position.z, -limit, limit);

    const groundY = this.build.ground.heightAt(root.position.x, root.position.z);
    const rise = groundY - this.footY;
    if (rise > 0.05 && this.stepArmed) {
      character.step(Math.min(1, rise / 0.28));
      this.stepArmed = false;
    } else if (rise < 0.02) {
      this.stepArmed = true;
    }
    this.footY += (groundY - this.footY) * (1 - Math.exp(-delta / 0.048));
    root.position.y = this.footY + this.footOffset;

    this.announceArrival(root.position);
    this.explored.reveal(root.position.x, root.position.z, this.sightRadius);

    if (this.velocity.lengthSq() > 0.02) {
      const targetAngle = Math.atan2(this.velocity.x, this.velocity.z);
      const difference = ((targetAngle - root.rotation.y + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      root.rotation.y += difference * Math.min(1, MOVE.turnRate * delta);
    }

    character.update(delta, this.velocity.length(), this.elapsed);
    if (this.debugPose !== undefined) character.setPose(this.debugPose, 1, this.elapsed);
    for (const tree of this.build.trees) tree.sway(this.elapsed);

    this.iso.follow(root.position);
    this.iso.update(delta);

    if (this.sunAnchor.distanceToSquared(root.position) > LIGHT.shadowExtent * LIGHT.shadowExtent * 0.1) {
      this.sunAnchor.copy(root.position);
      this.lighting.focus.copy(root.position);
      this.lighting.requestUpdate();
    }

    this.markerAge += delta;
    const markerFade = 1 - this.markerAge / 0.45;
    (this.marker.material as THREE.MeshBasicMaterial).opacity = Math.max(0, markerFade);
    this.marker.visible = markerFade > 0;

    this.syncPeers(delta);
    this.toast.update(delta);
  }

  private syncPeers(delta: number): void {
    const session = this.session;
    if (!session) {
      // no world: the chest is still there to be opened
      const width = this.canvas.clientWidth || window.innerWidth;
      const height = this.canvas.clientHeight || window.innerHeight;
      this.updateInteraction(delta, width, height);
      return;
    }

    for (const invite of session.consumeAccepts()) {
      this.party.add(invite.id);
      this.invites.delete(invite.id);
    }
    for (const invite of session.consumeInvites()) {
      this.invites.set(invite.id, { name: invite.name, at: this.elapsed });
    }
    for (const [id, invite] of this.invites) {
      if (this.elapsed - invite.at > PARTY.inviteTimeout || !session.peers.has(id)) {
        this.invites.delete(id);
      }
    }

    const root = this.build.character.root;
    session.update(delta, {
      x: root.position.x,
      z: root.position.z,
      yaw: root.rotation.y,
      speed: this.velocity.length(),
      torch: this.build.character.torchCarried,
    });

    for (const [id, peer] of session.peers) {
      let remote = this.remotes.get(id);
      if (!remote) {
        remote = buildRemote(peer);
        this.scene.add(remote.root);
        this.remotes.set(id, remote);
        // their torch flame is accent geometry that the mask pass has not seen yet
        this.presenter.post.invalidateAccent();
      }
      remote.sync(delta, peer, this.build.ground, this.netLevel);
    }

    for (const [id, remote] of this.remotes) {
      if (session.peers.has(id)) continue;
      remote.dispose();
      this.remotes.delete(id);
      this.presenter.post.invalidateAccent();
    }

    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    for (const remote of this.remotes.values()) remote.project(this.iso.camera, width, height);

    this.updateInteraction(delta, width, height);
  }

  private updateInteraction(delta: number, width: number, height: number): void {
    const session = this.session;
    const root = this.build.character.root;
    const kind = this.keyboard.inputKind();
    const key: PromptKey =
      kind === "keyboard"
        ? "E"
        : kind === "xbox"
          ? "A"
          : kind === "playstation"
            ? "X"
            : kind === "touch"
              ? "tap"
              : null;

    let nearest: { id: string; name: string; distance: number } | null = null;
    if (session) {
      for (const [id, peer] of session.peers) {
        if (id === session.selfId || this.party.has(id)) continue;
        const distance = Math.hypot(peer.x - root.position.x, peer.z - root.position.z);
        if (distance > PARTY.reach) continue;
        if (!nearest || distance < nearest.distance) nearest = { id, name: peer.name, distance };
      }
    }

    const chest = this.build.chest;
    const chestDistance = Math.hypot(
      chest.group.position.x - root.position.x,
      chest.group.position.z - root.position.z,
    );
    const fireDistance = Math.hypot(
      FIRE.spot[0] - root.position.x,
      FIRE.spot[1] - root.position.z,
    );

    // whatever is nearest wins: the chest, the fire, or whoever is stood there
    let pick: { kind: "chest" | "fire" | "peer"; id: string; distance: number } | null = null;
    if (!chest.opened && chestDistance <= CHEST.reach) {
      pick = { kind: "chest", id: "chest", distance: chestDistance };
    }
    if (!this.owned.has("torch") && fireDistance <= FIRE.reach && (!pick || fireDistance < pick.distance)) {
      pick = { kind: "fire", id: "fire", distance: fireDistance };
    }
    if (nearest && (!pick || nearest.distance < pick.distance)) {
      pick = { kind: "peer", id: nearest.id, distance: nearest.distance };
    }

    const onChest = pick?.kind === "chest";
    const onFire = pick?.kind === "fire";
    const target = pick ? pick.id : null;

    if (target !== this.interactTarget) {
      this.interactTarget = target;
      this.holdTime = 0;
      this.holdFired = false;
      this.waitingOn = null;
    }

    const onPeer = pick?.kind === "peer";
    const pending = onPeer && nearest ? this.invites.get(nearest.id) : undefined;
    const waiting = onPeer && !pending && nearest !== null && this.waitingOn === nearest.id;
    const holdFor = onChest ? CHEST.hold : PARTY.hold;

    if (!this.keyboard.isInteractDown()) {
      this.holdTime = 0;
      this.holdFired = false;
    } else if (target && !this.holdFired) {
      this.holdTime += delta;
      if (this.holdTime >= holdFor) {
        this.holdTime = holdFor;
        this.holdFired = true;
        if (onChest) {
          this.openChest();
        } else if (onFire) {
          this.takeTorch();
        } else if (nearest && session) {
          if (pending) {
            session.accept(nearest.id);
            this.party.add(nearest.id);
            this.invites.delete(nearest.id);
          } else {
            session.invite(nearest.id);
            this.waitingOn = nearest.id;
          }
        }
      }
    }

    let label: string | null = null;
    if (onChest) label = "open chest";
    else if (onFire) label = "take torch";
    else if (pending) label = `accept invite from ${pending.name}`;
    else if (waiting) label = `waiting for ${nearest?.name ?? "them"}`;
    else if (nearest) label = `invite ${nearest.name} to party`;
    this.prompt.set(
      this.inventory.isOpen ? null : label,
      waiting ? null : key,
      waiting ? 0 : this.holdTime / holdFor,
    );

    // icons hold the plain daylight ramp. Following the live tints made them
    // shift with the accent well before the world itself looked dark.
    this.inventory.setTints(DITHER.shadowTint, DITHER.highlightTint);
    this.inventory.setChest(
      chest.opened
        ? chest.items.filter((item) => !item.taken).map((item) => item.id)
        : null,
    );

    this.toast.setLift(this.inventory.isOpen ? this.inventory.topInset() + 10 : 0);

    const night = this.netLevel < NIGHT.invertBelow;
    this.prompt.setNight(night);
    this.partyHud.setNight(night);
    this.inventory.set([...this.owned]);

    const roster: string[] = [];
    const targets = [];
    if (session) {
      for (const id of this.party) {
        const peer = session.peers.get(id);
        if (!peer) continue;
        roster.push(peer.name);
        this.projected.set(
          peer.x,
          this.build.ground.heightAt(peer.x, peer.z) + PARTY.headAnchor,
          peer.z,
        );
        this.projected.project(this.iso.camera);
        targets.push({
          id,
          name: peer.name,
          x: this.projected.x,
          y: this.projected.y,
          visible:
            this.projected.z < 1 &&
            Math.abs(this.projected.x) < PARTY.onScreen &&
            Math.abs(this.projected.y) < PARTY.onScreen,
        });
      }
    }
    this.partyHud.setRoster(roster);
    this.partyHud.setMarkers(targets, this.screenBox(width, height));
    this.updateHover();
  }

  private screenBox(width: number, height: number): ScreenBox {
    const rect = this.canvas.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width || width,
      height: rect.height || height,
      ...this.partyHud.safeInsets(),
    };
  }

  /** the reveal: the lid lifts, the embers catch, and two things come out */
  private openChest(): void {
    const chest = this.build.chest;
    if (chest.opened) return;
    chest.open();
    // the panel comes up with the lid, and keeps the world in view: you take
    // things out of the chest by hand
    this.inventory.show();
  }

  /** a torch from the fire; the prompt only appears while you have none */
  private takeTorch(): void {
    if (this.owned.has("torch")) return;
    this.owned.add("torch");
    this.toast.show("torch");
  }

  /** walking off, or shutting the panel, shuts the lid too */
  private closeChest(): void {
    const chest = this.build.chest;
    if (!chest.opened) return;
    chest.close();
    this.tooltip.hide();
  }

  /** put something back: it returns to its place in the box */
  private store(id: ItemId): void {
    const chest = this.build.chest;
    // with the lid shut there is nowhere to put it, and it would teleport in
    if (!chest.opened) return;
    const item = chest.items.find((entry) => entry.id === id);
    if (!item || !item.taken) return;
    chest.restore(item);
    this.owned.delete(id);
    this.applySight();
    this.toast.show(ITEMS[id].name);
  }

  /** you take things out of a chest yourself, one at a time */
  private collect(item: ChestItem): void {
    if (item.taken) return;
    this.build.chest.take(item);
    this.owned.add(item.id);
    if (item.id === "clock") flashClock();
    this.applySight();
    this.tooltip.hide();
    this.toast.show(ITEMS[item.id].name);
  }

  private pickChestItem(point: { x: number; y: number }): ChestItem | null {
    const chest = this.build.chest;
    if (!chest.opened) return null;
    const live = chest.items.filter((item) => !item.taken);
    if (live.length === 0) return null;

    const rect = this.canvas.getBoundingClientRect();
    const pointer = new THREE.Vector2(
      ((point.x - rect.left) / rect.width) * 2 - 1,
      -(((point.y - rect.top) / rect.height) * 2 - 1),
    );
    this.picker.setFromCamera(pointer, this.iso.camera);
    const hits = this.picker.intersectObjects(
      live.map((item) => item.root),
      true,
    );
    const hit = hits[0]?.object;
    if (!hit) return null;

    for (const item of live) {
      let node: THREE.Object3D | null = hit;
      while (node) {
        if (node === item.root) return item;
        node = node.parent;
      }
    }
    return null;
  }

  private updateHover(): void {
    // the panel owns the tooltip while it is up
    if (this.inventory.isOpen) return;
    const chest = this.build.chest;
    if (!chest.opened || this.inventory.isOpen) {
      this.tooltip.hide();
      return;
    }
    const item = this.pickChestItem(this.pointer);
    if (item) this.tooltip.show(ITEMS[item.id].name, this.pointer.x, this.pointer.y);
    else this.tooltip.hide();
  }

  /**
   * Only the glasses move the camera. Checking ownership against a flag rather
   * than the blend matters: taking the clock while the glasses were still easing
   * out used to trigger a second zoom.
   */
  private applySight(): void {
    const on = this.owned.has("glasses");
    if (on === this.glassesOn) return;
    this.glassesOn = on;
    // all the way out, so the radius you just bought is unmistakable
    this.iso.zoomTo(on && SIGHT.zoomToMax ? CAMERA.maxFrustum : CAMERA.frustumHeight);
  }

  /** the radius itself eases out over a couple of seconds, so you watch it go */
  private growSight(delta: number): void {
    const want = this.owned.has("glasses") ? 1 : 0;
    if (Math.abs(want - this.sightBlend) < 0.001) {
      this.sightBlend = want;
      return;
    }
    const step = delta / Math.max(0.2, SIGHT.seconds);
    this.sightBlend = Math.min(
      1,
      Math.max(0, this.sightBlend + Math.sign(want - this.sightBlend) * step),
    );
    const eased = this.sightBlend * this.sightBlend * (3 - 2 * this.sightBlend);
    const bonus = 1 + (SIGHT.bonus - 1) * eased;
    // the fog of war opens wider than the blur radius, so the world gives up more
    this.sightRadius = EXPLORED.radius * (1 + (SIGHT.exploreBonus - 1) * eased);
    this.presenter.post.configureBlur({
      clearWorld: BLUR.clearWorld * bonus,
      blurWorld: BLUR.blurWorld * bonus,
    });

    // stamp the widening radius every frame, so the fog opens as a swell rather
    // than waiting for the player to take a step
    const root = this.build.character.root.position;
    this.explored.reveal(root.x, root.z, this.sightRadius, true);
  }

  get sky(): { hours: number; daylight: number; clockOwned: boolean } {
    const sky = sampleDay(this.clock);
    return {
      hours: this.clock * 24,
      daylight: Math.min(1, Math.max(0, sky.sun / LIGHT.intensity)),
      clockOwned: this.owned.has("clock"),
    };
  }

  private applyDaylight(delta: number): void {
    this.clock = (((Date.now() / 1000 + this.clockOffset) / this.cycle) % 1 + 1) % 1;
    const sky = sampleDay(this.clock);

    this.presenter.post.configure({
      shadowTint: sky.shadow,
      highlightTint: sky.highlight,
      fog: sky.fog,
    });

    const sun = this.lighting.sun;
    sun.intensity = sky.sun;
    sun.color.setHex(sky.sunColour);
    this.lighting.ambient.intensity = sky.ambient;
    this.lighting.hemisphere.intensity = sky.hemisphere;

    this.sunTimer += delta;
    const drift =
      Math.abs(sky.azimuth - this.sunAzimuth) + Math.abs(sky.elevation - this.sunElevation);
    if (this.sunTimer > 0.4 && drift > 0.08) {
      this.sunTimer = 0;
      this.sunAzimuth = sky.azimuth;
      this.sunElevation = sky.elevation;
      this.lighting.setSun(sky.azimuth, sky.elevation);
    }

    const daylight = Math.min(1, Math.max(0, sky.sun / LIGHT.intensity));
    this.netLevel = daylight;
    this.growSight(delta);
    this.build.fire.update(this.elapsed, daylight);
    this.build.chest.update(delta, this.elapsed, daylight);

    const character = this.build.character;
    // a torch has to be taken from a fire; owning one is what makes it come out
    character.setTorchCarried(this.owned.has("torch") && daylight < TORCH.carryBelow);
    character.torchGlow(daylight);
    this.toast.setNight(daylight < NIGHT.invertBelow);

    this.themeTimer.value += delta;
    if (this.themeTimer.value > 1 && sky.shadow !== this.themeTimer.colour) {
      this.themeTimer.value = 0;
      this.themeTimer.colour = sky.shadow;
      this.syncThemeColour(sky.shadow);
    }
  }

  private syncThemeColour(shadowTint: number): void {
    const edge = new THREE.Color(shadowTint).multiplyScalar(0.82);
    const hex = `#${[edge.r, edge.g, edge.b]
      .map((value) => Math.round(Math.min(1, Math.max(0, value)) * 255).toString(16).padStart(2, "0"))
      .join("")}`;
    document.documentElement.style.background = hex;
    document.body.style.background = hex;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", hex);
  }

  private viewFrame(): ViewFrame {
    const camera = this.iso.camera;
    const position = this.build.character.root.position;
    return {      worldHeight: this.iso.frustum,
      worldWidth: this.iso.frustum * (camera.right - camera.left) / (camera.top - camera.bottom),
      playerX: position.x,
      playerZ: position.z,
      worldSize: WORLD.groundSize,
      near: camera.near,
      far: camera.far,
      cameraMatrix: camera.matrixWorld,
    };
  }

  private clearWaypoint(): void {
    this.hasWaypoint = false;
  }

  private assignWaypoint(point: { x: number; y: number }): void {
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((point.x - rect.left) / rect.width) * 2 - 1,
      -(((point.y - rect.top) / rect.height) * 2 - 1),
    );
    this.raycaster.setFromCamera(ndc, this.iso.camera);

    const origin = this.raycaster.ray.origin;
    const direction = this.raycaster.ray.direction;
    if (Math.abs(direction.y) < 1e-4) return;

    let height = this.build.character.root.position.y;
    let x = origin.x;
    let z = origin.z;
    for (let i = 0; i < 8; i++) {
      const t = (height - origin.y) / direction.y;
      if (!Number.isFinite(t) || t <= 0) return;
      x = origin.x + direction.x * t;
      z = origin.z + direction.z * t;
      height = this.build.ground.heightAt(x, z);
    }

    const limit = WORLD.groundSize * 0.46;
    if (Math.abs(x) > limit || Math.abs(z) > limit) return;

    this.waypoint.set(x, height + 0.08, z);
    this.hasWaypoint = true;
    this.marker.position.copy(this.waypoint);
    this.markerAge = 0;
  }

  private announceArrival(position: THREE.Vector3): void {
    let current: string | null = null;
    let lingering = false;

    for (const building of this.build.buildings) {
      const distance = Math.hypot(building.x - position.x, building.z - position.z);
      if (distance <= building.radius) current = building.label;
      else if (distance <= building.radius * 1.18) lingering = true;
    }

    if (current !== null) {
      if (current !== this.announced) {
        this.announced = current;
        this.toast.show(current);
      }
      return;
    }

    if (!lingering) this.announced = null;
  }
}
